import {
  createContext,
  useCallback,
  useContext,
  useId,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type ReactNode,
  type Ref,
} from "react";
import { createPortal } from "react-dom";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { getModalPortalOwnerId, useAnchoredPanel } from "./overlay";

type ActionMenuTriggerProps = {
  ref: Ref<HTMLButtonElement>;
  onClick: () => void;
  "aria-expanded": boolean;
  "aria-haspopup": "menu";
  "aria-controls": string | undefined;
};

export type ActionMenuProps = Omit<ComponentPropsWithRef<"div">, "children"> & {
  renderTrigger: (props: ActionMenuTriggerProps) => ReactNode;
  children: ReactNode;
};

const ActionMenuContext = createContext<{ close: () => void } | null>(null);

export function ActionMenu({
  renderTrigger,
  children,
  ref,
  className,
  ...rest
}: ActionMenuProps) {
  const panelId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [modalPortalOwnerId, setModalPortalOwnerId] = useState<string>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setIsOpen(false), []);

  const { panelStyle } = useAnchoredPanel({
    isOpen,
    onClose: closeMenu,
    triggerRef,
    panelRef,
    placement: "below",
    align: "end",
    focusMode: "menu",
  });

  const toggleMenu = useCallback(() => {
    setIsOpen((previous) => {
      if (previous) {
        return false;
      }
      setModalPortalOwnerId(getModalPortalOwnerId(triggerRef.current));
      return true;
    });
  }, []);

  return (
    <ActionMenuContext.Provider value={{ close: closeMenu }}>
      <div ref={ref} className={cn("inline-flex", className)} {...rest}>
        {renderTrigger({
          ref: triggerRef,
          onClick: toggleMenu,
          "aria-expanded": isOpen,
          "aria-haspopup": "menu",
          "aria-controls": isOpen ? panelId : undefined,
        })}
      </div>
      {isOpen &&
        createPortal(
          <div
            id={panelId}
            ref={panelRef}
            style={panelStyle}
            {...(modalPortalOwnerId
              ? { "data-modal-portal": modalPortalOwnerId }
              : {})}
            className="z-50 w-52 animate-scale-in rounded-xl border border-surface-200 bg-surface-0 p-1 shadow-popover"
            role="menu"
          >
            {children}
          </div>,
          document.body,
        )}
    </ActionMenuContext.Provider>
  );
}

export type ActionMenuItemProps = {
  icon?: LucideIcon;
  iconClassName?: string;
  onSelect: () => void;
  children: ReactNode;
  /** `danger` = destructive action (cancel, delete). */
  tone?: "default" | "danger";
  /** Not selectable and skipped by arrow-key navigation. */
  disabled?: boolean;
  className?: string;
};

export function ActionMenuItem({
  icon: Icon,
  iconClassName,
  onSelect,
  children,
  tone = "default",
  disabled = false,
  className,
}: ActionMenuItemProps) {
  const menu = useContext(ActionMenuContext);

  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={() => {
        onSelect();
        menu?.close();
      }}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm leading-none transition-colors disabled:pointer-events-none disabled:opacity-50",
        tone === "danger"
          ? "text-error-600 hover:bg-error-50 hover:text-error-700"
          : "text-surface-700 hover:bg-surface-100 hover:text-surface-900",
        className,
      )}
    >
      {Icon ? (
        <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center">
          <Icon className={cn("h-4 w-4", iconClassName)} aria-hidden />
        </span>
      ) : null}
      <span className="min-w-0 flex-1">{children}</span>
    </button>
  );
}

export type { ActionMenuTriggerProps };
